import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
    Shield, AlertTriangle, DollarSign, TrendingDown, Sparkles, CheckCircle2,
    Filter, RefreshCw, Eye,
} from "lucide-react";
import { PageHeader } from "@/components/ui/PageHeader";
import { GlassCard } from "@/components/ui/GlassCard";
import { StatCard } from "@/components/ui/StatCard";
import { NxButton } from "@/components/ui/NxButton";
import { NxBadge } from "@/components/ui/NxBadge";
import { Skeleton } from "@/components/ui/Skeleton";
import { apiGet, apiPost, apiPatch } from "@/lib/api";
import { formatAOA } from "@/lib/utils";
import pt from "@/lib/i18n";

const SEVERITY_VARIANT = { low: "neutral", medium: "info", high: "warning", critical: "error" };
const STATUS_VARIANT = { open: "warning", under_investigation: "info", resolved: "success", dismissed: "neutral" };

const RevenuePage = () => {
    const [summary, setSummary] = useState(null);
    const [anomalies, setAnomalies] = useState(null);
    const [filters, setFilters] = useState({ severity: "", status: "", type: "" });
    const [selected, setSelected] = useState(null);
    const [aiLoading, setAiLoading] = useState(false);

    const load = async () => {
        try {
            const params = new URLSearchParams();
            if (filters.severity) params.set("severity", filters.severity);
            if (filters.status) params.set("status", filters.status);
            if (filters.type) params.set("anomaly_type", filters.type);
            const [s, list] = await Promise.all([
                apiGet("/revenue/anomalies/summary"),
                apiGet(`/revenue/anomalies${params.toString() ? "?" + params.toString() : ""}`),
            ]);
            setSummary(s);
            setAnomalies(list || []);
        } catch (e) {}
    };

    useEffect(() => { load(); }, [filters]);

    const runAINotes = async (id) => {
        setAiLoading(true);
        try {
            const r = await apiPost(`/revenue/anomalies/${id}/ai-notes`);
            setAnomalies((prev) => prev.map(a => a.id === id ? { ...a, ai_case_notes: r.ai_case_notes } : a));
            if (selected?.id === id) setSelected({ ...selected, ai_case_notes: r.ai_case_notes });
        } catch (e) {}
        setAiLoading(false);
    };

    const updateStatus = async (id, status) => {
        try {
            const r = await apiPatch(`/revenue/anomalies/${id}`, { status });
            setAnomalies((prev) => prev.map(a => a.id === id ? r : a));
            if (selected?.id === id) setSelected(r);
            await load();
        } catch (e) {}
    };

    return (
        <div data-testid="revenue-page" className="space-y-6">
            <PageHeader title={pt.revenue.title} subtitle={pt.revenue.subtitle} testId="revenue-header" />

            {/* KPIs */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="lg:col-span-2">
                    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
                        <GlassCard padding="p-6" style={{ borderColor: "rgba(192,57,43,0.32)", borderWidth: 1.5, background: "linear-gradient(135deg, rgba(255,247,237,0.96), rgba(248,235,221,0.85))" }}>
                            <p className="font-body text-xs uppercase tracking-[0.16em] text-[color:var(--slate-mid)] mb-2 flex items-center gap-1.5">
                                <AlertTriangle size={13} className="text-[color:var(--error)]" />
                                {pt.revenue.atRisk}
                            </p>
                            <p className="font-display text-4xl text-[color:var(--copper-dark)] tracking-tight" data-testid="kpi-at-risk-amount">
                                AOA {summary?.open_at_risk_aoa ? formatAOA(summary.open_at_risk_aoa) : "0"}
                            </p>
                            <p className="font-mono text-xs text-[color:var(--slate-mid)] mt-1">
                                {summary?.open_count || 0} {pt.revenue?.activeAnomalies || "anomalias activas"}
                            </p>
                        </GlassCard>
                    </motion.div>
                </div>
                <StatCard label={pt.revenue.openAnomalies} value={summary?.open_count ?? 0} index={1} testId="kpi-open-anomalies" />
                <StatCard label={pt.revenue.totalAnomalies} value={summary?.total_count ?? 0} index={2} testId="kpi-total-anomalies" />
            </div>

            {/* Filters */}
            <GlassCard padding="p-4">
                <div className="flex flex-wrap items-center gap-2">
                    <Filter size={14} className="text-[color:var(--slate-mid)]" />
                    <select
                        value={filters.severity}
                        onChange={(e) => setFilters({ ...filters, severity: e.target.value })}
                        className="px-2.5 py-1.5 rounded-md bg-white/80 border border-[color:var(--glass-border)] text-sm font-body text-[color:var(--slate-deep)]"
                        data-testid="filter-severity"
                    >
                        <option value="">{pt.revenue.filterSeverity}</option>
                        {Object.entries(pt.revenue.severities).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                    <select
                        value={filters.status}
                        onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                        className="px-2.5 py-1.5 rounded-md bg-white/80 border border-[color:var(--glass-border)] text-sm font-body text-[color:var(--slate-deep)]"
                        data-testid="filter-status"
                    >
                        <option value="">{pt.revenue.filterStatus}</option>
                        {Object.entries(pt.revenue.statuses).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                    <select
                        value={filters.type}
                        onChange={(e) => setFilters({ ...filters, type: e.target.value })}
                        className="px-2.5 py-1.5 rounded-md bg-white/80 border border-[color:var(--glass-border)] text-sm font-body text-[color:var(--slate-deep)]"
                        data-testid="filter-type"
                    >
                        <option value="">{pt.revenue.filterType}</option>
                        {Object.entries(pt.revenue.types).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                </div>
            </GlassCard>

            {/* Anomalies table */}
            <GlassCard padding="p-0" className="overflow-hidden">
                {anomalies === null ? (
                    <div className="p-4 space-y-2">{[1, 2, 3].map(i => <Skeleton key={i} className="h-14" />)}</div>
                ) : anomalies.length === 0 ? (
                    <p className="p-8 text-center font-body text-sm text-[color:var(--slate-mid)]">{pt.revenue.empty}</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm" data-testid="anomalies-table">
                            <thead className="bg-[color:var(--bg-surface)] text-[color:var(--slate-mid)] uppercase text-[10px] tracking-wider">
                                <tr>
                                    <th className="px-4 py-2.5 text-left">{pt.revenue.col_type}</th>
                                    <th className="px-4 py-2.5 text-left">{pt.revenue.col_severity}</th>
                                    <th className="px-4 py-2.5 text-left w-[40%]">{pt.revenue.col_description}</th>
                                    <th className="px-4 py-2.5 text-left">{pt.revenue.col_customer}</th>
                                    <th className="px-4 py-2.5 text-right">{pt.revenue.col_amount}</th>
                                    <th className="px-4 py-2.5 text-left">{pt.revenue.col_status}</th>
                                    <th className="px-4 py-2.5 text-right">{pt.revenue.col_actions}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {anomalies.map((a) => (
                                    <motion.tr
                                        key={a.id}
                                        initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                                        className="border-t border-[color:var(--glass-border)] hover:bg-[color:var(--copper-subtle)]/40 cursor-pointer"
                                        onClick={() => setSelected(a)}
                                        data-testid={`anomaly-row-${a.id}`}
                                    >
                                        <td className="px-4 py-2.5 font-mono text-[11px] text-[color:var(--copper-dark)] uppercase">{pt.revenue.types[a.anomaly_type]}</td>
                                        <td className="px-4 py-2.5"><NxBadge variant={SEVERITY_VARIANT[a.severity]}>{pt.revenue.severities[a.severity]}</NxBadge></td>
                                        <td className="px-4 py-2.5 font-body text-[color:var(--slate-deep)] leading-snug">{a.description}</td>
                                        <td className="px-4 py-2.5 font-body text-[color:var(--slate-mid)] text-xs">{a.customer_name || "—"}</td>
                                        <td className="px-4 py-2.5 text-right font-mono text-[color:var(--slate-deep)]">{a.amount_at_risk_aoa ? `AOA ${formatAOA(a.amount_at_risk_aoa)}` : "—"}</td>
                                        <td className="px-4 py-2.5"><NxBadge variant={STATUS_VARIANT[a.status]}>{pt.revenue.statuses[a.status]}</NxBadge></td>
                                        <td className="px-4 py-2.5 text-right">
                                            <Eye size={14} className="text-[color:var(--copper)] inline-block" />
                                        </td>
                                    </motion.tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </GlassCard>

            {/* Detail drawer */}
            {selected && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm flex items-end lg:items-center justify-center"
                    onClick={() => setSelected(null)}
                    data-testid="anomaly-drawer"
                >
                    <motion.div
                        initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
                        className="w-full lg:w-2/3 lg:max-w-3xl max-h-[90vh] overflow-y-auto bg-[color:var(--bg-surface)] rounded-t-2xl lg:rounded-2xl p-6 lg:p-8 border-t-2 lg:border-2 border-[color:var(--copper)]"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-start justify-between mb-4">
                            <div>
                                <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-[color:var(--copper-dark)]">
                                    {pt.revenue.types[selected.anomaly_type]}
                                </p>
                                <h2 className="font-display text-2xl text-[color:var(--slate-deep)] tracking-tight mt-1">{selected.description}</h2>
                                <div className="flex items-center gap-2 mt-2">
                                    <NxBadge variant={SEVERITY_VARIANT[selected.severity]}>{pt.revenue.severities[selected.severity]}</NxBadge>
                                    <NxBadge variant={STATUS_VARIANT[selected.status]}>{pt.revenue.statuses[selected.status]}</NxBadge>
                                    {selected.customer_name && <span className="font-mono text-xs text-[color:var(--slate-mid)]">{selected.customer_name}</span>}
                                </div>
                            </div>
                            <button onClick={() => setSelected(null)} className="text-[color:var(--slate-mid)] hover:text-[color:var(--slate-deep)] text-2xl leading-none">&times;</button>
                        </div>

                        {selected.amount_at_risk_aoa > 0 && (
                            <div className="mb-4 p-4 rounded-lg bg-[color:var(--copper-subtle)] border border-[color:var(--copper)]">
                                <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-[color:var(--slate-mid)]">Em risco</p>
                                <p className="font-display text-3xl text-[color:var(--copper-dark)] mt-1">
                                    AOA {formatAOA(selected.amount_at_risk_aoa)}
                                </p>
                            </div>
                        )}

                        {/* AI notes */}
                        <div className="mb-4">
                            <div className="flex items-center justify-between mb-2">
                                <p className="font-body text-xs uppercase tracking-[0.14em] text-[color:var(--slate-mid)] flex items-center gap-1.5">
                                    <Sparkles size={11} className="text-[color:var(--copper)]" />
                                    {pt.revenue.aiCaseNotes}
                                </p>
                                <NxButton variant="copper" size="sm" onClick={() => runAINotes(selected.id)} disabled={aiLoading} icon={<RefreshCw size={11} className={aiLoading ? "animate-spin" : ""} />} data-testid="btn-ai-notes">
                                    {aiLoading ? "…" : pt.revenue.generateNotes}
                                </NxButton>
                            </div>
                            {selected.ai_case_notes ? (
                                <p className="font-body text-sm text-[color:var(--slate-deep)] leading-relaxed bg-white/60 p-4 rounded-md border border-[color:var(--glass-border)] whitespace-pre-wrap" data-testid="ai-notes-text">
                                    {selected.ai_case_notes}
                                </p>
                            ) : (
                                <p className="font-body text-sm text-[color:var(--slate-mid)] italic">{pt.common.noNotes}</p>
                            )}
                        </div>

                        {/* Status actions */}
                        <div className="flex gap-2 pt-4 border-t border-[color:var(--glass-border)]">
                            {selected.status === "open" && (
                                <NxButton variant="secondary" size="sm" onClick={() => updateStatus(selected.id, "under_investigation")} data-testid="btn-mark-investigation">
                                    {pt.revenue.markInvestigation}
                                </NxButton>
                            )}
                            {selected.status !== "resolved" && (
                                <NxButton variant="copper" size="sm" onClick={() => updateStatus(selected.id, "resolved")} icon={<CheckCircle2 size={13} />} data-testid="btn-mark-resolved">
                                    {pt.revenue.markResolved}
                                </NxButton>
                            )}
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </div>
    );
};

export default RevenuePage;
